import { useEffect } from 'react';

/**
 * Story 24.3 — `inert` on the app root while a modal layer is open.
 *
 * Radix's modal Dialog only sets `aria-hidden` on everything outside the
 * portalled content (`hideOthers`), which hides the app from screen readers
 * but leaves it focusable and clickable for everything else. `inert` removes
 * it from the tab order, hit-testing and find-in-page too.
 *
 * - The attribute goes on `#root` (index.html), which is a sibling of every
 *   Radix portal in `document.body`. The toast region and the announcer are
 *   portalled to `document.body` as well (Story 24.3), so their live regions
 *   stay outside the inert subtree.
 * - Ref-counted across stacked layers: a Dialog opened over a Sheet adds a
 *   second hold, and `inert` is removed only when the last layer unmounts.
 * - A no-op when there is no `#root` (most unit tests render into a bare
 *   container).
 */

const APP_ROOT_ID = 'root';

let holds = 0;

function appRoot(): HTMLElement | null {
  return typeof document === 'undefined' ? null : document.getElementById(APP_ROOT_ID);
}

function acquire(): void {
  holds += 1;
  appRoot()?.setAttribute('inert', '');
}

function release(): void {
  holds = Math.max(0, holds - 1);
  if (holds === 0) appRoot()?.removeAttribute('inert');
}

/** Hold `inert` on `#root` for as long as the calling component is mounted. */
export function useInertAppRoot(active = true): void {
  useEffect(() => {
    if (!active) return;
    acquire();
    return release;
  }, [active]);
}

/** Test-only: the current number of holds. */
export function inertHoldCount(): number {
  return holds;
}

/**
 * Holds `inert` on `#root` for as long as it is mounted. Every layer content
 * (Sheet, Dialog, AlertDialog) renders one inside its Radix Content, so the
 * hold lives exactly as long as the content does (including its exit
 * animation).
 */
export function InertAppRoot(): null {
  useInertAppRoot();
  return null;
}
